import React from "react";
import { MoreVertical, ArrowUp, ArrowDown, Pencil, Archive, Activity, GripVertical } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { filesApi } from "@/lib/api";
import LogoWithHalo from "@/components/LogoWithHalo";

/**
 * CompactEntityCard — high-density executive HQ card.
 *
 * Consumes the SAME derived `summary` shape that the detail card uses
 * (from /api/dashboard/entity_summaries) — no duplicate data path.
 * Adds a small health strip and up to two attention badges so the admin
 * can scan many entities without opening any of them.
 *
 * Never reads manual/deprecated fields (phase, current_priority,
 * next_action, upcoming_event) — those exist only on the entity model
 * for backwards-compat and are ignored on the homepage.
 */
export default function CompactEntityCard({ entity, summary, onOpen, onEdit, onArchive, onMoveUp, onMoveDown, dragHandleProps, isDragging }) {
  const accent = entity.color || "#3B82F6";
  const mode = entity.display_mode || "detail";
  const isLogoMode = mode === "logo" && entity.logo_file_id;
  const eyebrow = summary?.current_stage || entity.phase || entity.status || "Active";
  const priorityLine = summary?.priority_line;
  const nextLine = summary?.next_line;
  const health = summary?.health_summary || { on_track: 0, needs_attention: 0, at_risk: 0, overdue: 0 };
  const overdueTaskCount = summary?.overdue_task_count || 0;
  const decisionsCount = summary?.attention?.decisions_count || 0;
  const atRiskCount = (health.at_risk || 0) + (health.overdue || 0);
  const activeCount = summary?.active_project_count || 0;
  const hasUrgent = summary?.attention?.has_urgent;

  // Small attention chips — cap at two most-important. Dedupe so an entity
  // with 2 overdue tasks + 0 at-risk projects never shows the same problem
  // twice; project-health drives the At-Risk chip, tasks drive Overdue,
  // decisions get their own chip.
  const chips = [];
  if (atRiskCount > 0) chips.push({ key: "risk", label: `${atRiskCount} at risk`, color: "#EF4444" });
  if (overdueTaskCount > 0) chips.push({ key: "overdue", label: `${overdueTaskCount} overdue`, color: "#EC4899" });
  if (decisionsCount > 0) chips.push({ key: "dec", label: `${decisionsCount} decision${decisionsCount > 1 ? "s" : ""}`, color: "#F59E0B" });

  return (
    <div
      className={`relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-300 md:hover:-translate-y-0.5 md:hover:shadow-lg flex flex-col p-4 min-h-[140px] md:min-h-[168px] ${isDragging ? "opacity-40 shadow-none" : ""}`}
      style={{
        borderColor: hasUrgent ? `${accent}66` : "rgba(255,255,255,0.08)",
        backgroundColor: "#151A23",
      }}
      onClick={onOpen}
      data-testid={`entity-compact-card-${entity.id}`}
      data-view-mode="compact"
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = accent)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = hasUrgent ? `${accent}66` : "rgba(255,255,255,0.08)")}
    >
      {/* Faint accent stripe on the left edge for identity without swallowing the card */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: hasUrgent ? accent : `${accent}88` }}
      />

      {entity.is_archived && (
        <div className="absolute top-2 left-3 text-[9px] uppercase tracking-widest text-white/60 bg-white/10 px-1.5 py-0.5 rounded z-10">
          Archived
        </div>
      )}

      {/* Drag handle — only rendered when the parent grid is in reorder mode.
          Hit target is 32×32 (mobile-friendly). Uses a listener bag that stops
          click propagation so grabbing the handle NEVER opens the workspace. */}
      {dragHandleProps && (
        <button
          type="button"
          data-testid={`entity-compact-drag-${entity.id}`}
          aria-label={`Reorder ${entity.name}`}
          className="absolute top-1.5 left-1.5 z-20 w-8 h-8 rounded-md flex items-center justify-center text-white/35 hover:text-white hover:bg-white/10 focus:text-white focus:bg-white/10 transition-colors touch-none cursor-grab active:cursor-grabbing"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          {...dragHandleProps}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}

      <div className="absolute top-2 right-2 z-20" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`entity-compact-menu-${entity.id}`}
              className="w-7 h-7 rounded-md bg-white/[0.04] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#151A23] border-white/10 text-white">
            <DropdownMenuItem onClick={onEdit} data-testid={`entity-compact-edit-${entity.id}`} className="hover:bg-white/10 focus:bg-white/10">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMoveUp} className="hover:bg-white/10 focus:bg-white/10">
              <ArrowUp className="w-4 h-4 mr-2" /> Move up
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMoveDown} className="hover:bg-white/10 focus:bg-white/10">
              <ArrowDown className="w-4 h-4 mr-2" /> Move down
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem onClick={onArchive} data-testid={`entity-compact-archive-${entity.id}`} className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10">
              <Archive className="w-4 h-4 mr-2" /> Archive
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="relative z-10 pl-2 pr-8 flex-1 flex flex-col">
        {/* Header row — logo (if set) or eyebrow + name */}
        <div className="flex items-start gap-2.5">
          {isLogoMode && (
            <LogoWithHalo
              src={filesApi.downloadUrl(entity.logo_file_id)}
              alt=""
              variant="compact"
              wrapperClassName="w-8 h-8 md:w-10 md:h-10 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden"
              imgClassName="max-w-full max-h-full w-auto h-auto object-contain"
              testid={`entity-compact-logo-wrap-${entity.id}`}
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-white/45 font-medium truncate">
              {eyebrow}
            </div>
            <h3 className="font-display text-base md:text-lg font-semibold text-white leading-tight truncate">
              {entity.name}
            </h3>
          </div>
        </div>

        {/* Priority / Next block */}
        <div className="space-y-1 mt-3 flex-1" data-testid={`entity-compact-summary-${entity.id}`}>
          {priorityLine ? (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] md:text-[10px] uppercase tracking-wider text-white/40 pt-0.5 min-w-[54px] flex-shrink-0">Priority</span>
              <span className="text-xs md:text-[13px] text-white/85 leading-snug break-words line-clamp-2 flex-1">{priorityLine}</span>
            </div>
          ) : (summary && chips.length === 0 && !nextLine) ? (
            // Only surface "Nothing pressing." when the card is TRULY quiet —
            // otherwise the copy contradicts an attention chip that IS live.
            <div className="text-xs text-white/35 italic">Nothing pressing.</div>
          ) : null}
          {nextLine && (
            <div className="flex gap-2 items-start">
              <span className="text-[9px] md:text-[10px] uppercase tracking-wider text-white/40 pt-0.5 min-w-[54px] flex-shrink-0">Next</span>
              <span className="text-xs md:text-[13px] text-white/75 leading-snug break-words line-clamp-1 flex-1">{nextLine}</span>
            </div>
          )}
        </div>

        {/* Footer strip: health summary + attention chips */}
        <div className="mt-3 pt-2.5 border-t border-white/[0.06] flex items-center gap-2 flex-wrap">
          <HealthDots health={health} activeCount={activeCount} />
          {chips.slice(0, 2).map((c) => (
            <span
              key={c.key}
              data-testid={`entity-compact-chip-${entity.id}-${c.key}`}
              className="inline-flex items-center text-[9px] md:text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border font-medium"
              style={{ color: c.color, borderColor: `${c.color}44`, backgroundColor: `${c.color}10` }}
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function HealthDots({ health, activeCount }) {
  const total = health.on_track + health.needs_attention + health.at_risk + health.overdue;
  if (total === 0 && activeCount === 0) {
    return <span className="text-[9px] md:text-[10px] uppercase tracking-wider text-white/35">No active projects</span>;
  }
  if (total === 0) {
    return <span className="text-[9px] md:text-[10px] uppercase tracking-wider text-white/45">{activeCount} active</span>;
  }
  const dots = [
    { count: health.overdue, color: "#EF4444", label: "Overdue" },
    { count: health.at_risk, color: "#F97316", label: "At Risk" },
    { count: health.needs_attention, color: "#F59E0B", label: "Needs Attention" },
    { count: health.on_track, color: "#10B981", label: "On Track" },
  ].filter((d) => d.count > 0);
  return (
    <div className="flex items-center gap-1.5" title={`${total} active project${total > 1 ? "s" : ""}`}>
      <Activity className="w-3 h-3 text-white/40" />
      {dots.map((d) => (
        <span
          key={d.label}
          className="inline-flex items-center gap-1 text-[10px] md:text-[11px] text-white/70"
          title={`${d.count} ${d.label}`}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: d.color }} />
          <span className="tabular-nums">{d.count}</span>
        </span>
      ))}
    </div>
  );
}
